/**
 * Keeps one avatar per remote room member: loads the chosen body, feeds its pose buffer,
 * drives the rig, places the name tag and tells the voice mesh where each head is. In a
 * crowded lobby only the nearest FULL_BODY_AVATAR_LIMIT members get a full body; the rest are
 * simple capsules with a name tag.
 */
import { MouthDriver, syllables } from './jaw.ts';
import * as THREE from 'three';
import {
  FULL_BODY_AVATAR_LIMIT,
  type MemberInfo,
  type PoseData,
  type Reaction,
} from '@clinspace/shared';
import type { AvatarLoader, LoadedAvatar } from '../world/avatars.ts';
import { AvatarRig } from './AvatarRig.ts';
import { NameTag } from './NameTag.ts';
import { PoseBuffer } from './PoseBuffer.ts';

interface Remote {
  member: MemberInfo;
  group: THREE.Group;
  buffer: PoseBuffer;
  tag: NameTag;
  capsule: THREE.Group;
  body: LoadedAvatar | undefined;
  rig: AvatarRig | undefined;
  /** Moves the avatar's jaw while its owner speaks. */
  mouth: MouthDriver | undefined;
  loading: boolean;
  lastHead: { position: THREE.Vector3; quaternion: THREE.Quaternion };
  distance: number;
  fullBody: boolean;
  reaction?: { sprite: THREE.Sprite; startedAt: number };
}

export const REACTION_EMOJI: Record<Reaction, string> = {
  thumbsUp: '👍',
  clap: '👏',
  wave: '👋',
  question: '❓',
};
const REACTION_MS = 2500;

function makeCapsule(colour: THREE.ColorRepresentation): THREE.Group {
  const g = new THREE.Group();
  const material = new THREE.MeshStandardMaterial({
    color: colour,
    roughness: 0.6,
    metalness: 0.1,
    transparent: true,
    opacity: 0.85,
  });
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.9, 4, 12), material);
  body.position.y = 0.75;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 10), material);
  head.position.y = 1.52;
  g.add(body, head);
  return g;
}

export class RemoteAvatarManager {
  readonly group = new THREE.Group();
  private readonly remotes = new Map<string, Remote>();
  private readonly heads = new Map<
    string,
    { position: THREE.Vector3; quaternion: THREE.Quaternion }
  >();
  private readonly reactionTextures = new Map<Reaction, THREE.CanvasTexture>();
  /** Dev hook: the last reaction shown, by session. */
  lastReaction: { sessionId: string; reaction: Reaction; at: number } | undefined;

  /** Loudness of a remote person's voice as heard here (set by the app from voice chat). */
  voiceLevel: ((sessionId: string) => number | undefined) | undefined;

  constructor(
    private readonly loader: AvatarLoader,
    private readonly viewer: THREE.Camera,
  ) {
    this.group.name = 'remote-avatars';
  }

  get count(): number {
    return this.remotes.size;
  }

  /** Session ids with a loaded full body, for debugging. */
  loadedBodies(): string[] {
    return [...this.remotes.values()].filter((r) => r.rig).map((r) => r.member.sessionId);
  }

  headPositions(): Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion }> {
    return this.heads;
  }

  /* ---------- Membership ---------- */

  setMembers(
    members: MemberInfo[],
    selfId: string | undefined,
    poses: Record<string, PoseData> = {},
  ): void {
    const keep = new Set<string>();
    for (const m of members) {
      if (m.sessionId === selfId) continue;
      keep.add(m.sessionId);
      this.upsert(m);
      const p = poses[m.sessionId];
      if (p) this.remotes.get(m.sessionId)?.buffer.push(p);
    }
    for (const id of [...this.remotes.keys()]) if (!keep.has(id)) this.remove(id);
  }

  upsert(member: MemberInfo): void {
    const existing = this.remotes.get(member.sessionId);
    if (existing) {
      existing.member = member;
      existing.tag.setIdentity(member.name, member.role);
      existing.tag.setMuted(member.muted);
      return;
    }
    const group = new THREE.Group();
    group.name = `avatar-${member.sessionId}`;
    group.visible = false;
    const tag = new NameTag(member.name, member.role);
    tag.setMuted(member.muted);
    const capsule = makeCapsule('#7f8a98');
    capsule.visible = false;
    group.add(capsule, tag.sprite);
    this.group.add(group);
    const remote: Remote = {
      member,
      group,
      buffer: new PoseBuffer(),
      tag,
      capsule,
      body: undefined,
      rig: undefined,
      mouth: undefined,
      loading: false,
      lastHead: { position: new THREE.Vector3(0, 1.6, 0), quaternion: new THREE.Quaternion() },
      distance: Infinity,
      fullBody: true,
    };
    this.remotes.set(member.sessionId, remote);
    this.heads.set(member.sessionId, remote.lastHead);
    void this.loadBody(remote);
  }

  remove(id: string): void {
    const r = this.remotes.get(id);
    if (!r) return;
    this.remotes.delete(id);
    this.heads.delete(id);
    this.group.remove(r.group);
    r.tag.dispose();
    if (r.reaction) (r.reaction.sprite.material as THREE.Material).dispose();
    r.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry?.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const m of mats) if (!r.body || !isBodyMaterial(r.body, m)) m.dispose();
      }
    });
  }

  clear(): void {
    for (const id of [...this.remotes.keys()]) this.remove(id);
  }

  updateMember(
    id: string,
    patch: { muted?: boolean; height?: number; handRaised?: boolean },
  ): void {
    const r = this.remotes.get(id);
    if (!r) return;
    if (patch.muted !== undefined) {
      r.member.muted = patch.muted;
      r.tag.setMuted(patch.muted);
    }
    if (patch.height !== undefined) r.member.height = patch.height;
    if (patch.handRaised !== undefined) {
      r.member.handRaised = patch.handRaised;
      r.tag.setHandRaised(patch.handRaised);
    }
  }

  /** Names of the members with a hand up. */
  raisedHands(): string[] {
    return [...this.remotes.values()].filter((r) => r.member.handRaised).map((r) => r.member.name);
  }

  /** A reaction floats up from above the member's head and fades. */
  showReaction(id: string, reaction: Reaction, now = performance.now()): void {
    const r = this.remotes.get(id);
    if (!r) return;
    this.lastReaction = { sessionId: id, reaction, at: now };
    if (r.reaction) {
      r.group.remove(r.reaction.sprite);
      (r.reaction.sprite.material as THREE.Material).dispose();
    }
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.reactionTexture(reaction),
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    sprite.scale.set(0.28, 0.28, 1);
    sprite.renderOrder = 55;
    r.group.add(sprite);
    r.reaction = { sprite, startedAt: now };
  }

  private reactionTexture(reaction: Reaction): THREE.CanvasTexture {
    let t = this.reactionTextures.get(reaction);
    if (t) return t;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'rgba(18, 24, 34, 0.85)';
    ctx.beginPath();
    ctx.arc(64, 64, 60, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = '72px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffd37a';
    ctx.fillText(REACTION_EMOJI[reaction], 64, 70);
    t = new THREE.CanvasTexture(canvas);
    t.colorSpace = THREE.SRGBColorSpace;
    this.reactionTextures.set(reaction, t);
    return t;
  }

  onPose(id: string, pose: PoseData): void {
    this.remotes.get(id)?.buffer.push(pose);
  }

  private async loadBody(remote: Remote): Promise<void> {
    if (remote.loading || remote.body) return;
    remote.loading = true;
    const avatar = remote.member.avatar;
    try {
      let body: LoadedAvatar;
      if (avatar.startsWith('vrm:')) body = await this.loader.loadVrm(avatar.slice(4));
      else
        body = await this.loader.loadBuiltIn(isBuiltIn(avatar) ? avatar : 'avatar-01', {
          lod: 'lod1',
        });
      if (!this.remotes.has(remote.member.sessionId)) return;
      remote.body = body;
      remote.rig = new AvatarRig(body.root, body.vrm);
      remote.mouth = body.jaw ? new MouthDriver(body.jaw) : undefined;
      body.root.visible = false;
      remote.group.add(body.root);
    } catch (err) {
      console.warn(`[clinspace] avatar load failed for ${remote.member.name}:`, err);
    } finally {
      remote.loading = false;
    }
  }

  /* ---------- Per frame ---------- */

  update(dt: number): void {
    const now = performance.now();
    const viewerPos = this.viewer.getWorldPosition(new THREE.Vector3());
    const list = [...this.remotes.values()];
    for (const r of list) {
      const sample = r.buffer.sample(now);
      if (!sample) {
        r.group.visible = false;
        r.distance = Infinity;
        continue;
      }
      r.group.visible = true;
      r.lastHead.position.copy(sample.headPosition);
      r.lastHead.quaternion.copy(sample.headQuaternion);
      r.distance = sample.headPosition.distanceTo(viewerPos);
      r.tag.setSpeaking(sample.speaking && !r.member.muted);
      r.tag.setDistance(r.distance);
      r.tag.sprite.position.set(
        sample.headPosition.x,
        sample.headPosition.y + 0.32,
        sample.headPosition.z,
      );
      if (r.reaction) {
        const t = (now - r.reaction.startedAt) / REACTION_MS;
        if (t >= 1) {
          r.group.remove(r.reaction.sprite);
          (r.reaction.sprite.material as THREE.Material).dispose();
          r.reaction = undefined;
        } else {
          r.reaction.sprite.position.set(
            sample.headPosition.x,
            sample.headPosition.y + 0.55 + t * 0.35,
            sample.headPosition.z,
          );
          (r.reaction.sprite.material as THREE.SpriteMaterial).opacity =
            t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
        }
      }
      (r as Remote & { sample?: typeof sample }).sample = sample;
    }
    // Nearest N get full bodies.
    list.sort((a, b) => a.distance - b.distance);
    list.forEach((r, i) => {
      r.fullBody = i < FULL_BODY_AVATAR_LIMIT;
    });
    for (const r of list) {
      const sample = (r as Remote & { sample?: ReturnType<PoseBuffer['sample']> }).sample;
      if (!sample) continue;
      const useBody = r.fullBody && r.rig && r.body;
      if (useBody && r.body && r.rig) {
        r.body.root.visible = true;
        r.capsule.visible = false;
        r.rig.apply(sample, dt);
        // Their voice's loudness where we hear them, else a speaking rhythm while they talk.
        const speaking = sample.speaking && !r.member.muted;
        const heard = this.voiceLevel?.(r.member.sessionId);
        r.mouth?.update(
          speaking ? (heard ?? syllables(now / 1000, r.member.sessionId.charCodeAt(0)) * 0.6) : 0,
          dt,
        );
      } else {
        if (r.body) r.body.root.visible = false;
        r.capsule.visible = true;
        r.capsule.position.set(
          sample.headPosition.x,
          sample.headPosition.y - 1.52,
          sample.headPosition.z,
        );
        r.capsule.rotation.y = Math.atan2(
          -new THREE.Vector3(0, 0, -1).applyQuaternion(sample.headQuaternion).x,
          -new THREE.Vector3(0, 0, -1).applyQuaternion(sample.headQuaternion).z,
        );
      }
    }
  }
}

function isBuiltIn(
  id: string,
): id is 'avatar-01' | 'avatar-02' | 'avatar-03' | 'avatar-04' | 'avatar-05' | 'avatar-06' {
  return /^avatar-0[1-6]$/.test(id);
}

function isBodyMaterial(body: LoadedAvatar, material: THREE.Material): boolean {
  let found = false;
  body.root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      if (mats.includes(material)) found = true;
    }
  });
  return found;
}
